import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useConfig } from '../context/ConfigContext';
import { formatPhoneDisplay, formatPhoneInput, stripPhone } from '../utils/phoneFormat';
import usePullToRefresh from '../hooks/usePullToRefresh';
import { PRODUCT_TYPE } from '../config/product';

function Customers() {
  const navigate = useNavigate();
  const { user, logout, isAdmin, isDriver, isSuperAdmin, shouldHidePrices } = useAuth();
  const { getAppName } = useConfig();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showPrintView, setShowPrintView] = useState(false);
  const [expandedCustomers, setExpandedCustomers] = useState({});
  const [showPropertyModal, setShowPropertyModal] = useState(false);
  const [propertyParent, setPropertyParent] = useState(null);
  const [editingProperty, setEditingProperty] = useState(null);
  const [propertyFormData, setPropertyFormData] = useState({
    address: '',
    city: '',
    state: '',
    zip: '',
    lawnTierId: '',
    priceLawn: '50.00',
    saltPrice: '8.00',
    paymentMethodPreferred: 'square',
    driveway: true,
    sidewalks: false,
    walkways: false,
    salt: false,
    saltPreference: 'never',
    serviceType: 'lawn_care',
    notes: '',
  });

  // State for quick property service type updates
  const [updatingPropertyServiceType, setUpdatingPropertyServiceType] = useState(null);

  // Payment methods state
  const [paymentMethods, setPaymentMethods] = useState([
    { id: 'square', name: 'Square', enabled: true, isBuiltIn: true },
    { id: 'venmo', name: 'Venmo', enabled: true, isBuiltIn: true },
    { id: 'cash', name: 'Cash', enabled: true, isBuiltIn: true },
    { id: 'cashapp', name: 'Cash App', enabled: true, isBuiltIn: true },
  ]);

  // Lead sources state
  const [leadSources, setLeadSources] = useState([
    { id: 'facebook', name: 'Facebook', enabled: true, isBuiltIn: true },
    { id: 'craigslist', name: 'Craigslist', enabled: true, isBuiltIn: true },
    { id: 'flyer', name: 'Flyer', enabled: true, isBuiltIn: true },
    { id: 'yard_sign', name: 'Yard Sign', enabled: true, isBuiltIn: true },
    { id: 'referral', name: 'Friend/Referral', enabled: true, isBuiltIn: true },
    { id: 'google', name: 'Google', enabled: true, isBuiltIn: true },
    { id: 'nextdoor', name: 'Nextdoor', enabled: true, isBuiltIn: true },
    { id: 'other', name: 'Other', enabled: true, isBuiltIn: true },
  ]);

  // Pricing tiers state
  const [pricingTiers, setPricingTiers] = useState([]);

  // Portal invite state
  const [portalStatus, setPortalStatus] = useState({});
  const [invitingPortal, setInvitingPortal] = useState(null);

  // Get only enabled payment methods and lead sources
  const enabledPaymentMethods = paymentMethods.filter(m => m.enabled);
  const enabledLeadSources = leadSources.filter(s => s.enabled);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    lawnTierId: '',
    priceLawn: '50.00',
    saltPrice: '8.00',
    driveway: true,
    sidewalks: false,
    walkways: false,
    salt: false,
    paymentMethodPreferred: 'square',
    snowfallThreshold: 'any',
    servicePreference: 'on_call',
    serviceType: 'lawn_care',
    referredById: '',
    billingSameAsService: true,
    billingName: '',
    billingAddress: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    notes: '',
    leadSource: '',
    discountAmount: '',
    discountType: '',
    discountReason: '',
    emailSubscribed: true,
  });

  // State for quick service type updates
  const [updatingServiceType, setUpdatingServiceType] = useState(null);

  const loadCustomers = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (showInactive) {
        // When checked, show ONLY inactive customers
        params.append('active', 'false');
      } else {
        // When unchecked, show active customers only
        params.append('active', 'true');
      }
      // Include children to get property data
      params.append('includeChildren', 'true');
      // Filter by product type (lawn, snow, or both)
      params.append('product', PRODUCT_TYPE);
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/customers?${params.toString()}`
      );
      setCustomers(response.data);
    } catch (err) {
      console.error('Failed to load customers:', err);
    } finally {
      setLoading(false);
    }
  }, [showInactive]);

  const loadPricingTiers = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/pricing-tiers?product=lawn`);
      setPricingTiers(response.data);
    } catch (err) {
      console.error('Failed to load pricing tiers:', err);
    }
  }, []);

  const loadPaymentMethods = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/payment-methods`);
      if (response.data && response.data.length > 0) {
        setPaymentMethods(response.data);
      }
    } catch (err) {
      console.error('Failed to load payment methods:', err);
    }
  }, []);

  const loadLeadSources = useCallback(async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/settings/lead-sources`);
      if (response.data && response.data.length > 0) {
        setLeadSources(response.data);
      }
    } catch (err) {
      console.error('Failed to load lead sources:', err);
    }
  }, []);

  const { PullToRefreshIndicator } = usePullToRefresh(loadCustomers);

  // Helper to calculate discounted price
  const calculateDiscountedPrice = (price, discountAmount, discountType) => {
    if (!discountAmount || discountAmount <= 0) return price;
    if (discountType === 'percent') {
      return Math.max(0, price - (price * discountAmount / 100));
    }
    return Math.max(0, price - discountAmount);
  };

  useEffect(() => {
    loadCustomers();
    loadPricingTiers();
    loadPaymentMethods();
    loadLeadSources();
  }, [loadCustomers, loadPricingTiers, loadPaymentMethods, loadLeadSources]);

  const handleSearch = (e) => {
    setSearch(e.target.value);
  };

  const filteredCustomers = customers.filter((customer) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      customer.first_name?.toLowerCase().includes(searchLower) ||
      customer.last_name?.toLowerCase().includes(searchLower) ||
      customer.address?.toLowerCase().includes(searchLower) ||
      customer.city?.toLowerCase().includes(searchLower)
    );
  });

  const handlePrint = () => {
    window.print();
  };

  const getPaymentMethodName = (methodId) => {
    const method = paymentMethods.find(m => m.id === methodId);
    return method ? method.name : methodId;
  };

  // Helper to get lawn tier name from ID
  const getLawnTierName = (tierId) => {
    const tier = pricingTiers.find(t => t.id === tierId);
    return tier ? tier.name : null;
  };


  const openAddModal = () => {
    setEditingCustomer(null);
    // Use first pricing tier as default, fallback to 'custom'
    const defaultTier = pricingTiers.length > 0 ? pricingTiers[0] : null;
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      state: '',
      zip: '',
      lawnTierId: defaultTier ? defaultTier.id : null,
      priceLawn: defaultTier ? parseFloat(defaultTier.default_price).toFixed(2) : '50.00',
      saltPrice: defaultTier ? parseFloat(defaultTier.salt_price || 0).toFixed(2) : '8.00',
      driveway: true,
      sidewalks: false,
      walkways: false,
      salt: false,
      paymentMethodPreferred: 'square',
      snowfallThreshold: 'any',
      servicePreference: 'on_call',
      serviceType: 'lawn_care',
      referredById: '',
      billingSameAsService: true,
      billingName: '',
      billingAddress: '',
      billingCity: '',
      billingState: '',
      billingZip: '',
      notes: '',
      leadSource: '',
      discountAmount: '',
      discountType: '',
      discountReason: '',
      emailSubscribed: true,
    });
    setError('');
    setShowModal(true);
  };

  const openEditModal = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      firstName: customer.first_name || '',
      lastName: customer.last_name || '',
      email: customer.email || '',
      phone: formatPhoneDisplay(customer.phone) || '',
      address: customer.address || '',
      city: customer.city || '',
      state: customer.state || '',
      zip: customer.zip || '',
      lawnTierId: customer.lawn_tier_id || null,
      priceLawn: customer.price_lawn?.toString() || '50.00',
      saltPrice: customer.salt_price?.toString() || '0.00',
      driveway: customer.driveway ?? true,
      sidewalks: customer.sidewalks ?? false,
      walkways: customer.walkways ?? false,
      salt: customer.salt ?? false,
      saltPreference: customer.salt_preference || 'never',
      paymentMethodPreferred: customer.payment_method_preferred || 'square',
      snowfallThreshold: customer.snowfall_threshold || 'any',
      servicePreference: customer.service_preference || 'on_call',
      serviceType: customer.service_type || 'lawn_care',
      referredById: customer.referred_by_id?.toString() || '',
      billingSameAsService: customer.billing_same_as_service ?? true,
      billingName: customer.billing_name || '',
      billingAddress: customer.billing_address || '',
      billingCity: customer.billing_city || '',
      billingState: customer.billing_state || '',
      billingZip: customer.billing_zip || '',
      notes: customer.notes || '',
      leadSource: customer.lead_source || '',
      discountAmount: customer.discount_amount?.toString() || '',
      discountType: customer.discount_type || '',
      discountReason: customer.discount_reason || '',
      emailSubscribed: customer.email_subscribed ?? true,
    });
    setError('');
    setShowModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Handle phone number formatting
    if (name === 'phone' || name === 'billingPhone') {
      setFormData((prev) => ({
        ...prev,
        [name]: formatPhoneInput(value),
      }));
      return;
    }

    // Handle tier changes - auto-fill prices from dynamic pricing tiers
    if (name === 'lawnTierId') {
      let newPriceLawn = formData.priceLawn;
      let newSaltPrice = formData.saltPrice;

      if (value) {
        // Find the selected tier from pricingTiers
        const selectedTier = pricingTiers.find(t => t.id === parseInt(value));
        if (selectedTier) {
          newPriceLawn = parseFloat(selectedTier.default_price).toFixed(2);
          newSaltPrice = parseFloat(selectedTier.salt_price || 0).toFixed(2);
        }
      }
      // For null/empty, keep existing prices (custom pricing)

      setFormData((prev) => ({
        ...prev,
        lawnTierId: value ? parseInt(value) : null,
        priceLawn: newPriceLawn,
        saltPrice: newSaltPrice,
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
        phone: stripPhone(formData.phone),
        address: formData.address,
        city: formData.city,
        state: formData.state,
        zip: formData.zip,
        lawnTierId: formData.lawnTierId,
        priceLawn: parseFloat(formData.priceLawn) || 50.00,
        saltPrice: parseFloat(formData.saltPrice) || 0,
        driveway: formData.driveway,
        sidewalks: formData.sidewalks,
        walkways: formData.walkways,
        salt: formData.saltPreference === 'always',
        saltPreference: formData.saltPreference,
        paymentMethodPreferred: formData.paymentMethodPreferred,
        snowfallThreshold: formData.snowfallThreshold || 'any',
        servicePreference: formData.servicePreference || 'on_call',
        referredById: formData.referredById ? parseInt(formData.referredById) : null,
        billingSameAsService: formData.billingSameAsService,
        billingName: formData.billingSameAsService ? null : formData.billingName,
        billingAddress: formData.billingSameAsService ? null : formData.billingAddress,
        billingCity: formData.billingSameAsService ? null : formData.billingCity,
        billingState: formData.billingSameAsService ? null : formData.billingState,
        billingZip: formData.billingSameAsService ? null : formData.billingZip,
        notes: formData.notes,
        leadSource: formData.leadSource || null,
        serviceType: formData.serviceType || 'lawn_care',
        isActive: true,
        discountAmount: formData.discountType ? parseFloat(formData.discountAmount) || null : null,
        discountType: formData.discountType || null,
        discountReason: formData.discountType ? formData.discountReason : null,
        emailSubscribed: formData.emailSubscribed,
      };

      if (editingCustomer) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/customers/${editingCustomer.id}`,
          payload
        );
      } else {
        await axios.post(`${process.env.REACT_APP_API_URL}/api/customers`, payload);
      }

      setShowModal(false);
      loadCustomers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save customer');
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (customer) => {
    if (!window.confirm(`Deactivate ${customer.first_name} ${customer.last_name}? They will be hidden but can be reactivated later.`)) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}`);
      loadCustomers();
    } catch (err) {
      alert('Failed to deactivate customer');
    }
  };

  const handlePermanentDelete = async (customer) => {
    if (!window.confirm(`PERMANENTLY DELETE ${customer.first_name} ${customer.last_name}?\n\nThis cannot be undone. All customer data will be removed.`)) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}?permanent=true`);
      loadCustomers();
      alert('Customer permanently deleted');
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete customer');
    }
  };

  const handleReactivate = async (customer) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}`, {
        ...customer,
        firstName: customer.first_name,
        lastName: customer.last_name,
        priceLawn: customer.price_lawn,
        paymentMethodPreferred: customer.payment_method_preferred,
        isActive: true,
      });
      loadCustomers();
    } catch (err) {
      alert('Failed to reactivate customer');
    }
  };

  // Quick service type change handler
  const handleQuickServiceTypeChange = async (customer, newServiceType) => {
    setUpdatingServiceType(customer.id);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${customer.id}`, {
        firstName: customer.first_name,
        lastName: customer.last_name,
        email: customer.email,
        phone: customer.phone,
        address: customer.address,
        city: customer.city,
        state: customer.state,
        zip: customer.zip,
        lawnTierId: customer.lawn_tier_id,
        priceLawn: customer.price_lawn,
        saltPrice: customer.salt_price,
        paymentMethodPreferred: customer.payment_method_preferred,
        serviceType: newServiceType,
        isActive: customer.is_active,
      });
      loadCustomers();
    } catch (err) {
      alert('Failed to update service type');
    } finally {
      setUpdatingServiceType(null);
    }
  };

  // Portal invite functions
  const loadPortalStatus = async (customerId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/customers/${customerId}/portal-status`
      );
      setPortalStatus(prev => ({ ...prev, [customerId]: response.data }));
    } catch (err) {
      console.error('Failed to load portal status:', err);
    }
  };

  const handlePortalInvite = async (customer) => {
    if (!customer.email) {
      alert('Customer does not have an email address. Please add an email first.');
      return;
    }

    setInvitingPortal(customer.id);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/portal/invite/${customer.id}`
      );

      if (response.data.inviteSent) {
        alert(`Portal invitation sent to ${customer.email}!`);
      } else if (response.data.inviteUrl) {
        // Copy URL to clipboard and show message
        try {
          await navigator.clipboard.writeText(response.data.inviteUrl);
          alert(`Email not configured. Invite link copied to clipboard:\n\n${response.data.inviteUrl}`);
        } catch {
          alert(`Email not configured. Share this link with the customer:\n\n${response.data.inviteUrl}`);
        }
      }

      // Refresh portal status
      loadPortalStatus(customer.id);
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to send portal invitation');
    } finally {
      setInvitingPortal(null);
    }
  };

  const getPortalStatusBadge = (customer) => {
    const status = portalStatus[customer.id];
    if (!status) return null;

    if (status.status === 'active') {
      return <span className="portal-badge active">Portal Active</span>;
    } else if (status.status === 'invited') {
      return <span className="portal-badge invited">Invite Sent</span>;
    }
    return null;
  };

  const getServiceBadges = (customer) => {
    const services = [];
    if (customer.driveway) services.push('Mowing');
    if (customer.sidewalks) services.push('Edging');
    if (customer.walkways) services.push('Trimming');
    if (customer.salt) services.push('Fertilizer');
    return services;
  };

  const toggleExpanded = (customerId) => {
    setExpandedCustomers(prev => ({
      ...prev,
      [customerId]: !prev[customerId]
    }));
  };

  const openAddPropertyModal = (customer) => {
    setPropertyParent(customer);
    setEditingProperty(null);
    // Use first pricing tier as default
    const defaultTier = pricingTiers.length > 0 ? pricingTiers[0] : null;
    setPropertyFormData({
      address: '',
      city: customer.city || '',
      state: customer.state || '',
      zip: '',
      lawnTierId: defaultTier ? defaultTier.id : null,
      priceLawn: defaultTier ? parseFloat(defaultTier.default_price).toFixed(2) : '50.00',
      saltPrice: defaultTier ? parseFloat(defaultTier.salt_price || 0).toFixed(2) : '8.00',
      paymentMethodPreferred: customer.payment_method_preferred || 'square',
      driveway: true,
      sidewalks: false,
      walkways: false,
      salt: false,
      saltPreference: 'never',
      serviceType: customer.service_type || 'lawn_care',
      notes: '',
    });
    setError('');
    setShowPropertyModal(true);
  };

  const openEditPropertyModal = (customer, property) => {
    setPropertyParent(customer);
    setEditingProperty(property);
    setPropertyFormData({
      address: property.address || '',
      city: property.city || '',
      state: property.state || '',
      zip: property.zip || '',
      lawnTierId: property.lawn_tier_id || null,
      priceLawn: property.price_lawn?.toString() || '50.00',
      saltPrice: property.salt_price?.toString() || '0.00',
      paymentMethodPreferred: property.payment_method_preferred || 'square',
      driveway: property.driveway ?? true,
      sidewalks: property.sidewalks ?? false,
      walkways: property.walkways ?? false,
      salt: property.salt ?? false,
      saltPreference: property.salt_preference || 'never',
      serviceType: property.service_type || 'lawn_care',
      notes: property.notes || '',
    });
    setError('');
    setShowPropertyModal(true);
  };

  const handlePropertyFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Auto-fill prices when tier changes using dynamic pricing tiers
    if (name === 'lawnTierId') {
      if (value) {
        const selectedTier = pricingTiers.find(t => t.id === parseInt(value));
        if (selectedTier) {
          setPropertyFormData(prev => ({
            ...prev,
            lawnTierId: parseInt(value),
            priceLawn: parseFloat(selectedTier.default_price).toFixed(2),
            saltPrice: parseFloat(selectedTier.salt_price || 0).toFixed(2),
          }));
          return;
        }
      }
      // Custom pricing - just update the tier id
      setPropertyFormData(prev => ({ ...prev, lawnTierId: null }));
      return;
    }

    setPropertyFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handlePropertySubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');

    try {
      const payload = {
        address: propertyFormData.address,
        city: propertyFormData.city,
        state: propertyFormData.state,
        zip: propertyFormData.zip,
        lawnTierId: propertyFormData.lawnTierId,
        priceLawn: parseFloat(propertyFormData.priceLawn) || 50.00,
        saltPrice: parseFloat(propertyFormData.saltPrice) || 0,
        paymentMethodPreferred: propertyFormData.paymentMethodPreferred,
        driveway: propertyFormData.driveway,
        sidewalks: propertyFormData.sidewalks,
        walkways: propertyFormData.walkways,
        salt: propertyFormData.saltPreference === 'always',
        saltPreference: propertyFormData.saltPreference,
        serviceType: propertyFormData.serviceType || 'lawn_care',
        notes: propertyFormData.notes,
      };

      if (editingProperty) {
        // Update existing property
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/customers/${editingProperty.id}`,
          {
            ...payload,
            firstName: propertyParent.first_name,
            lastName: propertyParent.last_name,
            email: propertyParent.email,
            phone: propertyParent.phone,
            isActive: true,
          }
        );
      } else {
        // Create new property
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/customers/${propertyParent.id}/properties`,
          payload
        );
      }

      setShowPropertyModal(false);
      loadCustomers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save property');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProperty = async (property) => {
    if (!window.confirm(`Delete this property at ${property.address}? This cannot be undone.`)) {
      return;
    }

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/api/customers/${property.id}?permanent=true`);
      loadCustomers();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to delete property');
    }
  };

  // Quick property service type change handler
  const handleQuickPropertyServiceTypeChange = async (property, newServiceType) => {
    setUpdatingPropertyServiceType(property.id);
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/api/customers/${property.id}`, {
        firstName: property.first_name,
        lastName: property.last_name,
        email: property.email,
        phone: property.phone,
        address: property.address,
        city: property.city,
        state: property.state,
        zip: property.zip,
        lawnTierId: property.lawn_tier_id,
        priceLawn: property.price_lawn,
        saltPrice: property.salt_price,
        paymentMethodPreferred: property.payment_method_preferred,
        serviceType: newServiceType,
        isActive: property.is_active,
      });
      loadCustomers();
    } catch (err) {
      alert('Failed to update property service type');
    } finally {
      setUpdatingPropertyServiceType(null);
    }
  };

  return (
    <div className="customers-page">
      {/* Header */}
      <header className="app-header">
        <div className="app-header-content">
          <span className="app-logo" onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            {getAppName()}
          </span>
          <nav className="header-nav desktop-nav">
            <button onClick={() => navigate('/dashboard')} className="nav-link">Dashboard</button>
            {!isDriver() && <span className="nav-link active">Customers</span>}
            <button onClick={() => navigate('/routes')} className="nav-link">Routes</button>
            {isAdmin() && <button onClick={() => navigate('/history')} className="nav-link">History</button>}
            {isAdmin() && <button onClick={() => navigate('/expenses')} className="nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => navigate('/reports')} className="nav-link">Reports</button>}
            <button onClick={() => navigate('/service-mode')} className="nav-link storm-link">Service Mode</button>
            <button onClick={() => navigate('/settings')} className="nav-link">Settings</button>
            <button onClick={() => navigate('/docs')} className="nav-link help-link" title="Help">?</button>
            {isSuperAdmin() && (
              <button onClick={() => navigate('/admin')} className="nav-link admin-link">Admin</button>
            )}
            <button onClick={logout} className="nav-link logout-link">Logout</button>
          </nav>
          <button className="mobile-menu-btn" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            <span></span>
            <span></span>
            <span></span>
          </button>
        </div>
        {mobileMenuOpen && (
          <nav className="mobile-nav">
            <button onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }} className="mobile-nav-link">Dashboard</button>
            {!isDriver() && <span className="mobile-nav-link active">Customers</span>}
            <button onClick={() => { navigate('/routes'); setMobileMenuOpen(false); }} className="mobile-nav-link">Routes</button>
            {isAdmin() && <button onClick={() => { navigate('/history'); setMobileMenuOpen(false); }} className="mobile-nav-link">History</button>}
            {isAdmin() && <button onClick={() => { navigate('/expenses'); setMobileMenuOpen(false); }} className="mobile-nav-link">Expenses</button>}
            {isAdmin() && <button onClick={() => { navigate('/reports'); setMobileMenuOpen(false); }} className="mobile-nav-link">Reports</button>}
            <button onClick={() => { navigate('/service-mode'); setMobileMenuOpen(false); }} className="mobile-nav-link storm-link">Service Mode</button>
            <button onClick={() => { navigate('/settings'); setMobileMenuOpen(false); }} className="mobile-nav-link">Settings</button>
            <button onClick={() => { navigate('/docs'); setMobileMenuOpen(false); }} className="mobile-nav-link">Help</button>
            {isSuperAdmin() && (
              <button onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }} className="mobile-nav-link admin-link">Admin</button>
            )}
            <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="mobile-nav-link logout-link">Logout</button>
          </nav>
        )}
      </header>

      <PullToRefreshIndicator />

      <div className="page-container">
        {/* Page Header */}
        <div className="page-header flex-between">
          <div>
            <h1 className="page-title">Customers</h1>
            <p className="text-muted">{filteredCustomers.length} customers</p>
          </div>
          {isAdmin() && (
            <button className="btn btn-primary" onClick={openAddModal}>
              + Add Customer
            </button>
          )}
        </div>

        {/* Filters */}
        <div className="filters-bar">
          <input
            type="text"
            className="form-input search-input"
            placeholder="Search by name, address..."
            value={search}
            onChange={handleSearch}
          />
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
            Show inactive only
          </label>
        </div>

        {/* Customer List */}
        {loading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
          </div>
        ) : filteredCustomers.length === 0 ? (
          <div className="empty-state">
            <p>No customers found</p>
            {isAdmin() && (
              <button className="btn btn-primary" onClick={openAddModal}>
                Add your first customer
              </button>
            )}
          </div>
        ) : (
          <div className="customer-list">
            {filteredCustomers.map((customer) => {
              // For LawnTrack: if customer is snow-only, display first lawn property's address/price
              const isCustomerLawnEligible = ['lawn_care', 'both'].includes(customer.service_type);
              const displayInfo = isCustomerLawnEligible
                ? customer
                : (customer.properties?.[0] || customer);

              // If displaying property as main, exclude it from additional properties list
              const remainingProperties = !isCustomerLawnEligible && customer.properties?.length > 0
                ? customer.properties.slice(1)
                : customer.properties || [];

              return (
              <div
                key={customer.id}
                className={`customer-card ${!customer.is_active ? 'inactive' : ''}`}
              >
                <div className="customer-card-header">
                  <div className="customer-info">
                    <h3 className="customer-name">
                      {customer.first_name} {customer.last_name}
                      {!customer.is_active && <span className="inactive-badge">Inactive</span>}
                      {customer.from_intake_form && customer.intake_status === 'pending' && (
                        <span className="intake-badge">New - From Form</span>
                      )}
                      {remainingProperties.length > 0 && (
                        <span className="property-count-badge">
                          {remainingProperties.length} {remainingProperties.length === 1 ? 'property' : 'properties'}
                        </span>
                      )}
                      {getPortalStatusBadge(customer)}
                    </h3>
                    <p className="customer-address">{displayInfo.address}</p>
                    <p className="customer-city">{displayInfo.city}, {displayInfo.state} {displayInfo.zip}</p>
                  </div>
                  {!shouldHidePrices() && (
                    <div className="customer-price">
                      {displayInfo.discount_amount && displayInfo.discount_type ? (
                        <>
                          <span className="price-original">
                            ${(parseFloat(displayInfo.price_lawn || 50) + (displayInfo.salt ? parseFloat(displayInfo.salt_price || 0) : 0)).toFixed(2)}
                          </span>
                          <span className="price-discounted">
                            ${calculateDiscountedPrice(
                              parseFloat(displayInfo.price_lawn || 50) + (displayInfo.salt ? parseFloat(displayInfo.salt_price || 0) : 0),
                              parseFloat(displayInfo.discount_amount),
                              displayInfo.discount_type
                            ).toFixed(2)}
                          </span>
                        </>
                      ) : (
                        `$${(parseFloat(displayInfo.price_lawn || 50) + (displayInfo.salt ? parseFloat(displayInfo.salt_price || 0) : 0)).toFixed(2)}`
                      )}
                      {displayInfo.salt && <span className="salt-badge-small">Fertilizer</span>}
                    </div>
                  )}
                </div>

                {!shouldHidePrices() && displayInfo.discount_amount && displayInfo.discount_type && (
                  <div className="discount-info-row">
                    <span className="discount-label">Discount:</span>
                    <span className="discount-value">
                      {displayInfo.discount_type === 'percent' ? `${displayInfo.discount_amount}% off` : `$${parseFloat(displayInfo.discount_amount).toFixed(2)} off`}
                    </span>
                    {displayInfo.discount_reason && (
                      <span className="discount-reason">- {displayInfo.discount_reason}</span>
                    )}
                  </div>
                )}

                <div className="customer-card-body">
                  {customer.phone && (
                    <a href={`tel:${customer.phone}`} className="customer-phone">
                      {formatPhoneDisplay(customer.phone)}
                    </a>
                  )}
                  {customer.referred_by_name && (
                    <p className="referred-by-text">Referred by: {customer.referred_by_name}</p>
                  )}
                  <div className="service-badges">
                    {/* Service Type Badge with Quick Edit - uses displayInfo for property-as-main scenarios */}
                    {isAdmin() ? (
                      <select
                        className={`service-type-select ${
                          displayInfo.service_type === 'lawn_care' ? 'lawn' :
                          displayInfo.service_type === 'both' ? 'both' : 'snow'
                        }`}
                        value={displayInfo.service_type || 'lawn_care'}
                        onChange={(e) => isCustomerLawnEligible
                          ? handleQuickServiceTypeChange(customer, e.target.value)
                          : handleQuickPropertyServiceTypeChange(displayInfo, e.target.value)
                        }
                        disabled={isCustomerLawnEligible
                          ? updatingServiceType === customer.id
                          : updatingPropertyServiceType === displayInfo.id
                        }
                        onClick={(e) => e.stopPropagation()}
                      >
                        <option value="lawn_care">Lawn</option>
                        <option value="both">Both</option>
                      </select>
                    ) : (
                      <span className={`service-type-badge ${
                        displayInfo.service_type === 'lawn_care' ? 'lawn' :
                        displayInfo.service_type === 'both' ? 'both' : 'lawn'
                      }`}>
                        {displayInfo.service_type === 'lawn_care' ? 'Lawn' :
                         displayInfo.service_type === 'both' ? 'Both' : 'Lawn'}
                      </span>
                    )}
                    {displayInfo.lawn_tier_id && getLawnTierName(displayInfo.lawn_tier_id) && (
                      <span className="tier-badge">{getLawnTierName(displayInfo.lawn_tier_id)}</span>
                    )}
                    <span className={`frequency-badge ${displayInfo.service_preference === 'seasonal' ? 'seasonal' : 'as-needed'}`}>
                      {displayInfo.service_preference === 'seasonal' ? 'Seasonal' : 'On-Call'}
                    </span>
                    {getServiceBadges(displayInfo).map((service) => (
                      <span key={service} className="service-badge">{service}</span>
                    ))}
                    {customer.referral_count > 0 && (
                      <span className="referral-badge">{customer.referral_count} referral{customer.referral_count !== 1 ? 's' : ''}</span>
                    )}
                    {customer.lead_source && (
                      <span className="lead-source-badge">
                        {leadSources.find(s => s.id === customer.lead_source)?.name || customer.lead_source}
                      </span>
                    )}
                  </div>
                </div>

                <div className="customer-card-footer">
                  <div className="payment-method">
                    {displayInfo.payment_method_preferred === 'square' && 'Square'}
                    {displayInfo.payment_method_preferred === 'venmo' && 'Venmo'}
                    {displayInfo.payment_method_preferred === 'cash' && 'Cash'}
                    {displayInfo.payment_method_preferred === 'cashapp' && 'Cash App'}
                  </div>
                  {isAdmin() && (
                    <div className="customer-actions">
                      <button
                        className="btn btn-sm btn-property"
                        onClick={() => openAddPropertyModal(customer)}
                        title="Add another property for this customer"
                      >
                        + Property
                      </button>
                      <button className="btn btn-sm btn-outline" onClick={() => openEditModal(customer)}>
                        Edit
                      </button>
                      {customer.email && (
                        <button
                          className="btn btn-sm btn-portal"
                          onClick={() => handlePortalInvite(customer)}
                          disabled={invitingPortal === customer.id || portalStatus[customer.id]?.status === 'active'}
                          title={portalStatus[customer.id]?.status === 'active' ? 'Customer already has portal access' : 'Invite customer to portal'}
                        >
                          {invitingPortal === customer.id ? 'Sending...' :
                           portalStatus[customer.id]?.status === 'active' ? 'Portal Active' :
                           portalStatus[customer.id]?.status === 'invited' ? 'Resend Invite' : 'Invite to Portal'}
                        </button>
                      )}
                      {customer.is_active ? (
                        <button className="btn btn-sm btn-outline" onClick={() => handleDeactivate(customer)}>
                          Deactivate
                        </button>
                      ) : (
                        <button className="btn btn-sm btn-success" onClick={() => handleReactivate(customer)}>
                          Reactivate
                        </button>
                      )}
                      <button className="btn btn-sm btn-danger" onClick={() => handlePermanentDelete(customer)}>
                        Delete
                      </button>
                    </div>
                  )}
                </div>

                {/* Expand/Collapse for properties */}
                {remainingProperties.length > 0 && (
                  <>
                    <button
                      className="properties-toggle"
                      onClick={() => toggleExpanded(customer.id)}
                    >
                      {expandedCustomers[customer.id] ? '▼' : '▶'} {remainingProperties.length} Additional {remainingProperties.length === 1 ? 'Property' : 'Properties'}
                    </button>

                    {expandedCustomers[customer.id] && (
                      <div className="properties-list">
                        {remainingProperties.map((property) => (
                          <div key={property.id} className="property-card">
                            <div className="property-info">
                              <p className="property-address">{property.address}</p>
                              <p className="property-city">{property.city}, {property.state} {property.zip}</p>
                              <div className="property-services">
                                {/* Property Service Type Badge/Select */}
                                {isAdmin() ? (
                                  <select
                                    className={`service-type-select ${
                                      property.service_type === 'lawn_care' ? 'lawn' :
                                      property.service_type === 'both' ? 'both' : 'snow'
                                    }`}
                                    value={property.service_type || 'lawn_care'}
                                    onChange={(e) => handleQuickPropertyServiceTypeChange(property, e.target.value)}
                                    disabled={updatingPropertyServiceType === property.id}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    <option value="lawn_care">Lawn</option>
                                    <option value="both">Both</option>
                                  </select>
                                ) : (
                                  <span className={`service-type-badge ${
                                    property.service_type === 'lawn_care' ? 'lawn' :
                                    property.service_type === 'both' ? 'both' : 'lawn'
                                  }`}>
                                    {property.service_type === 'lawn_care' ? 'Lawn' :
                                     property.service_type === 'both' ? 'Both' : 'Lawn'}
                                  </span>
                                )}
                                {property.lawn_tier_id && getLawnTierName(property.lawn_tier_id) && (
                                  <span className="tier-badge-sm">{getLawnTierName(property.lawn_tier_id)}</span>
                                )}
                                {property.driveway && <span className="service-badge-sm">Mowing</span>}
                                {property.sidewalks && <span className="service-badge-sm">Edging</span>}
                                {property.walkways && <span className="service-badge-sm">Trimming</span>}
                                {property.salt && <span className="service-badge-sm salt">Fertilizer</span>}
                              </div>
                            </div>
                            <div className="property-pricing">
                              <div className="property-price">
                                ${(parseFloat(property.price_lawn || 50) + (property.salt ? parseFloat(property.salt_price || 0) : 0)).toFixed(2)}
                              </div>
                              <div className="property-payment">
                                {property.payment_method_preferred === 'square' && 'Square'}
                                {property.payment_method_preferred === 'venmo' && 'Venmo'}
                                {property.payment_method_preferred === 'cash' && 'Cash'}
                                {property.payment_method_preferred === 'cashapp' && 'Cash App'}
                              </div>
                            </div>
                            {isAdmin() && (
                              <div className="property-actions">
                                <button
                                  className="btn btn-xs btn-outline"
                                  onClick={() => openEditPropertyModal(customer, property)}
                                >
                                  Edit
                                </button>
                                <button
                                  className="btn btn-xs btn-danger"
                                  onClick={() => handleDeleteProperty(property)}
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Print View */}
      {showPrintView && (
        <div className="print-overlay">
          <div className="print-container">
            <div className="print-header no-print">
              <h2>Print Customer List</h2>
              <div className="print-actions">
                <button className="btn btn-primary" onClick={handlePrint}>
                  <svg className="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6z"/>
                  </svg>
                  Print
                </button>
                <button className="btn btn-secondary" onClick={() => setShowPrintView(false)}>
                  Close
                </button>
              </div>
            </div>

            <div className="print-content">
              <div className="print-page-header">
                <h1>{user?.companyName || 'Customer List'}</h1>
                <p className="print-date">Printed: {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                <p className="print-count">{filteredCustomers.filter(c => c.is_active !== false).length} Active Customers</p>
              </div>

              <div className="print-customer-list">
                {filteredCustomers.filter(c => c.is_active !== false).map((customer, index) => (
                  <div key={customer.id} className="print-customer-card">
                    <div className="print-customer-header">
                      <span className="print-customer-number">{index + 1}</span>
                      <h3 className="print-customer-name">
                        {customer.first_name} {customer.last_name}
                      </h3>
                      <span className="print-customer-price">
                        ${(parseFloat(customer.price_lawn || 50) + (customer.salt ? parseFloat(customer.salt_price || 0) : 0)).toFixed(2)}
                      </span>
                    </div>

                    <div className="print-customer-details">
                      <div className="print-detail-row">
                        <span className="print-label">Address:</span>
                        <span className="print-value">
                          {customer.address}, {customer.city}, {customer.state} {customer.zip}
                        </span>
                      </div>

                      {customer.phone && (
                        <div className="print-detail-row">
                          <span className="print-label">Phone:</span>
                          <span className="print-value">{formatPhoneDisplay(customer.phone)}</span>
                        </div>
                      )}

                      <div className="print-detail-row">
                        <span className="print-label">Services:</span>
                        <span className="print-value print-services">
                          {customer.driveway && <span className="print-service-tag">Mowing</span>}
                          {customer.sidewalks && <span className="print-service-tag">Edging</span>}
                          {customer.walkways && <span className="print-service-tag">Trimming</span>}
                          {customer.salt && <span className="print-service-tag salt">Fertilizer</span>}
                          {!customer.driveway && !customer.sidewalks && !customer.walkways && !customer.salt && (
                            <span className="print-service-tag">Standard</span>
                          )}
                        </span>
                      </div>

                      <div className="print-detail-row">
                        <span className="print-label">Payment:</span>
                        <span className="print-value">{getPaymentMethodName(customer.payment_method_preferred)}</span>
                      </div>

                      {customer.notes && (
                        <div className="print-detail-row print-notes">
                          <span className="print-label">Notes:</span>
                          <span className="print-value">{customer.notes}</span>
                        </div>
                      )}
                    </div>

                    {/* Properties */}
                    {customer.properties && customer.properties.length > 0 && (
                      <div className="print-properties">
                        <span className="print-properties-label">Additional Properties:</span>
                        {customer.properties.map((prop, propIdx) => (
                          <div key={prop.id} className="print-property">
                            <span className="print-property-address">
                              {propIdx + 1}. {prop.address}, {prop.city}, {prop.state} {prop.zip}
                            </span>
                            <span className="print-property-price">
                              ${(parseFloat(prop.price_lawn || 50) + (prop.salt ? parseFloat(prop.salt_price || 0) : 0)).toFixed(2)}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              <div className="print-footer">
                <p>Generated by {user?.companyName || 'LawnTrack'}</p>
              </div>
            </div>
          </div>
        </div>
      )}

            {/* Add/Edit Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingCustomer ? 'Edit Customer' : 'Add Customer'}</h2>
              <button className="modal-close" onClick={() => setShowModal(false)}>&times;</button>
            </div>

            <form onSubmit={handleSubmit}>
              {error && <div className="alert alert-error">{error}</div>}

              <div className="modal-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">First Name *</label>
                    <input
                      type="text"
                      name="firstName"
                      className="form-input"
                      value={formData.firstName}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Last Name *</label>
                    <input
                      type="text"
                      name="lastName"
                      className="form-input"
                      value={formData.lastName}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Address *</label>
                  <input
                    type="text"
                    name="address"
                    className="form-input"
                    value={formData.address}
                    onChange={handleFormChange}
                    required
                  />
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">City *</label>
                    <input
                      type="text"
                      name="city"
                      className="form-input"
                      value={formData.city}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State *</label>
                    <input
                      type="text"
                      name="state"
                      className="form-input"
                      value={formData.state}
                      onChange={handleFormChange}
                      maxLength={2}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ZIP *</label>
                    <input
                      type="text"
                      name="zip"
                      className="form-input"
                      value={formData.zip}
                      onChange={handleFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      className="form-input"
                      value={formData.phone}
                      onChange={handleFormChange}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      name="email"
                      className="form-input"
                      value={formData.email}
                      onChange={handleFormChange}
                    />
                  </div>
                </div>

                <div className="form-divider">Service Details</div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Tier</label>
                    <select
                      name="lawnTierId"
                      className="form-select"
                      value={formData.lawnTierId || ''}
                      onChange={handleFormChange}
                    >
                      {pricingTiers.map(tier => (
                        <option key={tier.id} value={tier.id}>
                          {tier.name} - ${parseFloat(tier.default_price).toFixed(2)}
                        </option>
                      ))}
                      <option value="">Custom</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Method</label>
                    <select
                      name="paymentMethodPreferred"
                      className="form-select"
                      value={formData.paymentMethodPreferred}
                      onChange={handleFormChange}
                    >
                      {enabledPaymentMethods.map(method => (
                        <option key={method.id} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">Base Price *</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        name="priceLawn"
                        className="form-input"
                        value={formData.priceLawn}
                        onChange={handleFormChange}
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fertilizer Price</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        name="saltPrice"
                        className="form-input"
                        value={formData.saltPrice}
                        onChange={handleFormChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Total</label>
                    <div className="total-price-display">
                      ${(parseFloat(formData.priceLawn || 0) + (formData.saltPreference === 'always' ? parseFloat(formData.saltPrice || 0) : 0)).toFixed(2)}
                      {formData.saltPreference === 'always' && <span className="salt-indicator">+ Fertilizer</span>}
                      {formData.saltPreference === 'as_needed' && <span className="salt-indicator">+ Fertilizer (as needed)</span>}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Service Preference</label>
                    <select
                      name="servicePreference"
                      className="form-select"
                      value={formData.servicePreference}
                      onChange={handleFormChange}
                    >
                      <option value="seasonal">Seasonal (contracted for the season)</option>
                      <option value="on_call">On-Call / As Needed</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service Type</label>
                    <select
                      name="serviceType"
                      className="form-select"
                      value={formData.serviceType}
                      onChange={handleFormChange}
                    >
                      <option value="lawn_care">Lawn Care</option>
                      <option value="both">Both Services</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">How did you find me?</label>
                  <select
                    name="leadSource"
                    className="form-select"
                    value={formData.leadSource}
                    onChange={handleFormChange}
                  >
                    <option value="">-- Select --</option>
                    {enabledLeadSources.map(source => (
                      <option key={source.id} value={source.id}>{source.name}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Referred By</label>
                  <select
                    name="referredById"
                    className="form-select"
                    value={formData.referredById}
                    onChange={handleFormChange}
                  >
                    <option value="">None</option>
                    {customers
                      .filter(c => !editingCustomer || c.id !== editingCustomer.id)
                      .sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`))
                      .map(c => (
                        <option key={c.id} value={c.id}>
                          {c.last_name}, {c.first_name}
                        </option>
                      ))
                    }
                  </select>
                  <small className="text-muted">Customer who referred this customer (for referral discounts)</small>
                </div>

                {editingCustomer && (() => {
                  const referrer = editingCustomer.referred_by_id
                    ? customers.find(c => c.id === editingCustomer.referred_by_id)
                    : null;
                  const referrals = customers.filter(c => c.referred_by_id === editingCustomer.id);

                  if (!referrer && referrals.length === 0) return null;

                  return (
                    <div className="form-group referrals-section">
                      {referrer && (
                        <div className="referred-by-display">
                          <label className="form-label">Referred by:</label>
                          <div className="referral-item">
                            <span className="referral-name">{referrer.first_name} {referrer.last_name}</span>
                            <span className={`referral-status ${referrer.is_active ? 'active' : 'inactive'}`}>
                              {referrer.is_active ? 'Active' : 'Inactive'}
                            </span>
                          </div>
                        </div>
                      )}
                      {referrals.length > 0 && (
                        <>
                          <label className="form-label" style={referrer ? {marginTop: '12px'} : {}}>
                            Referrals ({referrals.length}):
                          </label>
                          <div className="referrals-list">
                            {referrals.map(r => (
                              <div key={r.id} className="referral-item">
                                <span className="referral-name">{r.first_name} {r.last_name}</span>
                                <span className={`referral-status ${r.is_active ? 'active' : 'inactive'}`}>
                                  {r.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  );
                })()}

                <div className="form-group">
                  <label className="form-label">Services Included</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="driveway"
                        checked={formData.driveway}
                        onChange={handleFormChange}
                      />
                      Mowing
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="sidewalks"
                        checked={formData.sidewalks}
                        onChange={handleFormChange}
                      />
                      Edging
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="walkways"
                        checked={formData.walkways}
                        onChange={handleFormChange}
                      />
                      Trimming
                    </label>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Fertilizer Preference</label>
                  <select
                    name="saltPreference"
                    className="form-input"
                    value={formData.saltPreference}
                    onChange={handleFormChange}
                  >
                    <option value="never">No Fertilizer</option>
                    <option value="always">Always Fertilizer</option>
                    <option value="as_needed">Fertilizer As Needed</option>
                  </select>
                </div>

                <div className="form-divider">Billing Address</div>

                <div className="form-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="billingSameAsService"
                      checked={formData.billingSameAsService}
                      onChange={handleFormChange}
                    />
                    Billing address same as service address
                  </label>
                </div>

                {!formData.billingSameAsService && (
                  <>
                    <div className="form-group">
                      <label className="form-label">Billing Name</label>
                      <input
                        type="text"
                        name="billingName"
                        className="form-input"
                        value={formData.billingName}
                        onChange={handleFormChange}
                        placeholder="Name for invoice"
                      />
                    </div>

                    <div className="form-group">
                      <label className="form-label">Billing Address</label>
                      <input
                        type="text"
                        name="billingAddress"
                        className="form-input"
                        value={formData.billingAddress}
                        onChange={handleFormChange}
                      />
                    </div>

                    <div className="form-row form-row-3">
                      <div className="form-group">
                        <label className="form-label">City</label>
                        <input
                          type="text"
                          name="billingCity"
                          className="form-input"
                          value={formData.billingCity}
                          onChange={handleFormChange}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">State</label>
                        <input
                          type="text"
                          name="billingState"
                          className="form-input"
                          value={formData.billingState}
                          onChange={handleFormChange}
                          maxLength={2}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">ZIP</label>
                        <input
                          type="text"
                          name="billingZip"
                          className="form-input"
                          value={formData.billingZip}
                          onChange={handleFormChange}
                        />
                      </div>
                    </div>
                  </>
                )}

                {/* Discount Section */}
                <div className="form-section-title">Discount</div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Discount Type</label>
                    <select
                      name="discountType"
                      className="form-select"
                      value={formData.discountType}
                      onChange={handleFormChange}
                    >
                      <option value="">None</option>
                      <option value="dollar">$ (Dollar)</option>
                      <option value="percent">% (Percent)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Amount</label>
                    <input
                      type="number"
                      name="discountAmount"
                      className="form-input"
                      value={formData.discountAmount}
                      onChange={handleFormChange}
                      placeholder={formData.discountType === 'percent' ? '10' : '5.00'}
                      step={formData.discountType === 'percent' ? '1' : '0.01'}
                      min="0"
                      disabled={!formData.discountType}
                    />
                  </div>
                </div>
                {formData.discountType && (
                  <div className="form-group">
                    <label className="form-label">Reason</label>
                    <input
                      type="text"
                      name="discountReason"
                      className="form-input"
                      value={formData.discountReason}
                      onChange={handleFormChange}
                      placeholder="e.g., Senior discount, Referral bonus"
                    />
                  </div>
                )}
                {formData.discountType && formData.discountAmount && (
                  <div className="discount-preview">
                    <span className="preview-label">Price Preview:</span>
                    <span className="original-price">${parseFloat(formData.priceLawn || 0).toFixed(2)}</span>
                    <span className="arrow">→</span>
                    <span className="discounted-price">
                      ${calculateDiscountedPrice(
                        parseFloat(formData.priceLawn || 0),
                        parseFloat(formData.discountAmount || 0),
                        formData.discountType
                      ).toFixed(2)}
                    </span>
                    <span className="discount-info">
                      ({formData.discountType === 'percent' ? `${formData.discountAmount}% off` : `$${formData.discountAmount} off`})
                    </span>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    name="notes"
                    className="form-textarea"
                    value={formData.notes}
                    onChange={handleFormChange}
                    rows={3}
                    placeholder="Special instructions, gate codes, etc."
                  />
                </div>

                <div className="form-group">
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      name="emailSubscribed"
                      checked={formData.emailSubscribed}
                      onChange={handleFormChange}
                      style={{ marginTop: '3px' }}
                    />
                    <div>
                      <span style={{ fontWeight: 500 }}>Subscribe to email updates</span>
                      <p style={{ fontSize: '13px', color: '#666', margin: '2px 0 0' }}>
                        Customer will receive service updates, seasonal announcements, and important notifications
                      </p>
                    </div>
                  </label>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingCustomer ? 'Save Changes' : 'Add Customer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Property Modal */}
      {showPropertyModal && (
        <div className="modal-overlay" onClick={() => setShowPropertyModal(false)}>
          <div className="modal modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{editingProperty ? 'Edit Property' : 'Add Property'}</h2>
              <button className="modal-close" onClick={() => setShowPropertyModal(false)}>&times;</button>
            </div>

            <form onSubmit={handlePropertySubmit}>
              {error && <div className="alert alert-error">{error}</div>}

              <div className="modal-body">
                {propertyParent && (
                  <div className="property-parent-info">
                    <span className="parent-label">Customer:</span>
                    <span className="parent-name">{propertyParent.first_name} {propertyParent.last_name}</span>
                  </div>
                )}

                <div className="form-group">
                  <label className="form-label">Address *</label>
                  <input
                    type="text"
                    name="address"
                    className="form-input"
                    value={propertyFormData.address}
                    onChange={handlePropertyFormChange}
                    required
                  />
                </div>

                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label className="form-label">City *</label>
                    <input
                      type="text"
                      name="city"
                      className="form-input"
                      value={propertyFormData.city}
                      onChange={handlePropertyFormChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">State *</label>
                    <input
                      type="text"
                      name="state"
                      className="form-input"
                      value={propertyFormData.state}
                      onChange={handlePropertyFormChange}
                      maxLength={2}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">ZIP *</label>
                    <input
                      type="text"
                      name="zip"
                      className="form-input"
                      value={propertyFormData.zip}
                      onChange={handlePropertyFormChange}
                      required
                    />
                  </div>
                </div>

                <div className="form-divider">Pricing & Services</div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Property Tier</label>
                    <select
                      name="lawnTierId"
                      className="form-select"
                      value={propertyFormData.lawnTierId || ''}
                      onChange={handlePropertyFormChange}
                    >
                      {pricingTiers.map(tier => (
                        <option key={tier.id} value={tier.id}>
                          {tier.name} - ${parseFloat(tier.default_price).toFixed(2)}
                        </option>
                      ))}
                      <option value="">Custom</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Payment Method</label>
                    <select
                      name="paymentMethodPreferred"
                      className="form-select"
                      value={propertyFormData.paymentMethodPreferred}
                      onChange={handlePropertyFormChange}
                    >
                      {enabledPaymentMethods.map(method => (
                        <option key={method.id} value={method.id}>{method.name}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Base Price *</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        name="priceLawn"
                        className="form-input"
                        value={propertyFormData.priceLawn}
                        onChange={handlePropertyFormChange}
                        step="0.01"
                        min="0"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Fertilizer Price</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">$</span>
                      <input
                        type="number"
                        name="saltPrice"
                        className="form-input"
                        value={propertyFormData.saltPrice}
                        onChange={handlePropertyFormChange}
                        step="0.01"
                        min="0"
                      />
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Services Included</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="driveway"
                        checked={propertyFormData.driveway}
                        onChange={handlePropertyFormChange}
                      />
                      Mowing
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="sidewalks"
                        checked={propertyFormData.sidewalks}
                        onChange={handlePropertyFormChange}
                      />
                      Edging
                    </label>
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        name="walkways"
                        checked={propertyFormData.walkways}
                        onChange={handlePropertyFormChange}
                      />
                      Trimming
                    </label>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Fertilizer Preference</label>
                    <select
                      name="saltPreference"
                      className="form-input"
                      value={propertyFormData.saltPreference}
                      onChange={handlePropertyFormChange}
                    >
                      <option value="never">No Fertilizer</option>
                      <option value="always">Always Fertilizer</option>
                      <option value="as_needed">Fertilizer As Needed</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Service Type</label>
                    <select
                      name="serviceType"
                      className="form-input"
                      value={propertyFormData.serviceType}
                      onChange={handlePropertyFormChange}
                    >
                      <option value="lawn_care">Lawn Care</option>
                      <option value="both">Both Services</option>
                    </select>
                  </div>
                </div>

                <div className="form-group">
                  <label className="form-label">Notes</label>
                  <textarea
                    name="notes"
                    className="form-textarea"
                    value={propertyFormData.notes}
                    onChange={handlePropertyFormChange}
                    rows={2}
                    placeholder="Special instructions for this property"
                  />
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline" onClick={() => setShowPropertyModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : editingProperty ? 'Save Changes' : 'Add Property'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        .header-nav {
          display: flex;
          align-items: center;
          gap: 4px;
        }
        .nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 8px 12px;
          border-radius: 6px;
          cursor: pointer;
          font-size: 0.875rem;
          transition: all 0.15s ease;
        }
        .nav-link:hover {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .nav-link.active {
          background: rgba(255,255,255,0.2);
          color: white;
        }
        .storm-link {
          background: var(--color-secondary) !important;
          color: white !important;
        }
        .storm-link:hover {
          background: var(--color-secondary-dark) !important;
        }
        .admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
          color: white !important;
        }
        .admin-link:hover {
          background: linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%) !important;
        }
        .logout-link {
          margin-left: 8px;
          border-left: 1px solid rgba(255,255,255,0.2);
          padding-left: 16px;
        }

        .flex-between {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          flex-wrap: wrap;
          gap: 16px;
        }

        .filters-bar {
          display: flex;
          gap: 16px;
          align-items: center;
          margin-bottom: 16px;
          flex-wrap: wrap;
        }
        .search-input {
          flex: 1;
          min-width: 200px;
          max-width: 400px;
        }
        /* Checkbox styles in index.css */

        .empty-state {
          text-align: center;
          padding: 48px 16px;
          color: var(--color-text-light);
        }
        .empty-state .btn {
          margin-top: 16px;
        }

        .customer-list {
          display: grid;
          gap: 16px;
        }

        .customer-card {
          background: var(--color-card);
          border-radius: 12px;
          padding: 16px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.08);
        }
        .customer-card.inactive {
          opacity: 0.6;
          background: #f9f9f9;
        }

        .customer-card-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }
        .customer-name {
          font-size: 1.125rem;
          font-weight: 600;
          margin: 0 0 4px 0;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .inactive-badge {
          font-size: 0.75rem;
          background: #999;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .intake-badge {
          font-size: 0.7rem;
          background: #f59e0b;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .portal-badge {
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .portal-badge.active {
          background: #dcfce7;
          color: #15803d;
        }
        .portal-badge.invited {
          background: #dbeafe;
          color: #1d4ed8;
        }
        .btn-portal {
          background: linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%) !important;
          color: white !important;
          border: none !important;
        }
        .btn-portal:hover:not(:disabled) {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%) !important;
        }
        .btn-portal:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .customer-address, .customer-city {
          margin: 0;
          color: var(--color-text-light);
          font-size: 0.875rem;
        }
        .customer-price {
          font-size: 1.5rem;
          font-weight: 700;
          color: var(--color-primary);
        }
        .customer-price .price-original {
          text-decoration: line-through;
          color: #9ca3af;
          font-size: 1rem;
          margin-right: 6px;
        }
        .customer-price .price-discounted {
          color: #059669;
        }
        .discount-info-row {
          font-size: 0.8rem;
          color: #059669;
          padding: 4px 12px;
          background: rgba(5, 150, 105, 0.08);
          margin: 0 -12px;
          display: flex;
          gap: 4px;
          flex-wrap: wrap;
        }
        .discount-label {
          font-weight: 600;
        }
        .discount-reason {
          color: #6b7280;
          font-style: italic;
        }

        .customer-card-body {
          margin-bottom: 12px;
        }
        .customer-phone {
          display: inline-block;
          color: var(--color-primary);
          margin-bottom: 8px;
        }
        .referred-by-text {
          margin: 0 0 8px 0;
          font-size: 0.85rem;
          color: #059669;
          font-style: italic;
        }
        .service-badges {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
        }
        .service-badge {
          font-size: 0.75rem;
          background: var(--color-primary-light);
          color: var(--color-primary-dark);
          padding: 4px 8px;
          border-radius: 4px;
        }
        .referral-badge {
          font-size: 0.75rem;
          background: #10b981;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .lead-source-badge {
          font-size: 0.75rem;
          background: #8b5cf6;
          color: white;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .frequency-badge {
          font-size: 0.75rem;
          padding: 4px 10px;
          border-radius: 12px;
          font-weight: 500;
        }
        .frequency-badge.seasonal {
          background: #dbeafe;
          color: #1e40af;
        }
        .frequency-badge.as-needed {
          background: #fef3c7;
          color: #92400e;
        }
        .threshold-badge {
          font-size: 0.75rem;
          background: #e5e7eb;
          color: #374151;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .service-pref-badge {
          font-size: 0.75rem;
          padding: 4px 8px;
          border-radius: 4px;
          font-weight: 500;
        }
        .service-pref-badge.seasonal {
          background: #dcfce7;
          color: #166534;
        }

        .customer-card-footer {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding-top: 12px;
          border-top: 1px solid var(--color-border);
        }
        .payment-method {
          font-size: 0.875rem;
          color: var(--color-text-light);
        }
        .customer-actions {
          display: flex;
          gap: 8px;
        }

        /* Modal Styles */
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.5);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          padding: 16px;
        }
        .modal {
          background: white;
          border-radius: 12px;
          max-width: 600px;
          width: 100%;
          max-height: 90vh;
          overflow-y: auto;
        }
        .modal-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 20px;
          border-bottom: 1px solid var(--color-border);
        }
        .modal-header h2 {
          margin: 0;
          font-size: 1.25rem;
        }
        .modal-close {
          background: none;
          border: none;
          font-size: 1.5rem;
          cursor: pointer;
          color: var(--color-text-light);
          line-height: 1;
        }
        .modal-body {
          padding: 20px;
        }
        .modal-footer {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding: 16px 20px;
          border-top: 1px solid var(--color-border);
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .form-row-3 {
          grid-template-columns: 2fr 1fr 1fr;
        }
        .form-section-title {
          font-weight: 600;
          color: var(--color-text);
          margin: 20px 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--color-border);
          font-size: 0.95rem;
        }
        .discount-preview {
          background: rgba(5, 150, 105, 0.08);
          padding: 12px;
          border-radius: 8px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          flex-wrap: wrap;
        }
        .discount-preview .preview-label {
          font-weight: 500;
          color: #374151;
        }
        .discount-preview .original-price {
          text-decoration: line-through;
          color: #9ca3af;
        }
        .discount-preview .arrow {
          color: #9ca3af;
        }
        .discount-preview .discounted-price {
          font-weight: 700;
          color: #059669;
          font-size: 1.1rem;
        }
        .discount-preview .discount-info {
          font-size: 0.85rem;
          color: #6b7280;
        }
        .form-divider {
          font-weight: 600;
          color: var(--color-text);
          margin: 20px 0 12px;
          padding-bottom: 8px;
          border-bottom: 1px solid var(--color-border);
        }
        .input-with-prefix {
          display: flex;
          align-items: center;
        }
        .input-prefix {
          background: var(--color-background);
          padding: 0 12px;
          height: 44px;
          display: flex;
          align-items: center;
          border: 1px solid var(--color-border);
          border-right: none;
          border-radius: 8px 0 0 8px;
          color: var(--color-text-light);
        }
        .input-with-prefix .form-input {
          border-radius: 0 8px 8px 0;
        }
        /* checkbox-group styles in index.css */
        .form-textarea {
          resize: vertical;
          min-height: 80px;
        }

        .total-price-display {
          display: flex;
          align-items: center;
          gap: 8px;
          height: 44px;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--color-primary);
        }
        .salt-indicator {
          font-size: 0.75rem;
          font-weight: 500;
          color: #059669;
          background: #d1fae5;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .salt-badge-small {
          display: inline-block;
          font-size: 0.65rem;
          font-weight: 600;
          background: #d1fae5;
          color: #059669;
          padding: 2px 6px;
          border-radius: 4px;
          margin-left: 6px;
          vertical-align: middle;
        }
        .tier-badge {
          background: #e0e7ff;
          color: #3730a3;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.75rem;
          font-weight: 500;
        }

        /* Mobile Menu */
        .mobile-menu-btn {
          display: none;
          flex-direction: column;
          justify-content: space-between;
          width: 24px;
          height: 18px;
          background: transparent;
          border: none;
          cursor: pointer;
          padding: 0;
        }
        .mobile-menu-btn span {
          display: block;
          width: 100%;
          height: 2px;
          background: white;
          border-radius: 1px;
        }
        .mobile-nav {
          display: flex;
          flex-direction: column;
          padding: 8px 16px 16px;
          border-top: 1px solid rgba(255,255,255,0.1);
        }
        .mobile-nav-link {
          background: transparent;
          border: none;
          color: rgba(255,255,255,0.8);
          padding: 12px 8px;
          text-align: left;
          font-size: 1rem;
          cursor: pointer;
          border-radius: 6px;
          transition: all 0.15s ease;
        }
        .mobile-nav-link:hover, .mobile-nav-link.active {
          background: rgba(255,255,255,0.1);
          color: white;
        }
        .mobile-nav-link.storm-link {
          background: var(--color-secondary);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.admin-link {
          background: linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%);
          color: white;
          margin-top: 8px;
        }
        .mobile-nav-link.logout-link {
          margin-top: 8px;
          border-top: 1px solid rgba(255,255,255,0.1);
          padding-top: 16px;
        }

        .referrals-section {
          background: #f0fdf4;
          border: 1px solid #bbf7d0;
          border-radius: 8px;
          padding: 12px;
          margin-top: 8px;
        }
        .referrals-section .form-label {
          color: #166534;
          margin-bottom: 8px;
        }
        .referrals-list {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .referral-item {
          display: flex;
          justify-content: space-between;
          align-items: center;
          background: white;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid #e5e7eb;
        }
        .referral-name {
          font-weight: 500;
          color: #374151;
        }
        .referral-status {
          font-size: 0.75rem;
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
        }
        .referral-status.active {
          background: #dcfce7;
          color: #166534;
        }
        .referral-status.inactive {
          background: #f3f4f6;
          color: #6b7280;
        }

        /* Property badge */
        .property-count-badge {
          font-size: 0.7rem;
          background: #7c3aed;
          color: white;
          padding: 2px 8px;
          border-radius: 4px;
          font-weight: 500;
        }

        /* Property button */
        .btn-property {
          background: #7c3aed;
          color: white;
          border: none;
        }
        .btn-property:hover {
          background: #6d28d9;
        }

        /* Properties toggle button */
        .properties-toggle {
          display: block;
          width: 100%;
          background: #f5f3ff;
          border: 1px solid #ddd6fe;
          border-radius: 6px;
          padding: 10px 16px;
          margin-top: 12px;
          cursor: pointer;
          text-align: left;
          font-size: 0.875rem;
          font-weight: 500;
          color: #7c3aed;
          transition: all 0.15s ease;
        }
        .properties-toggle:hover {
          background: #ede9fe;
        }

        /* Properties list */
        .properties-list {
          margin-top: 12px;
          padding-left: 16px;
          border-left: 3px solid #7c3aed;
        }

        .property-card {
          display: flex;
          align-items: center;
          gap: 16px;
          background: #faf5ff;
          border: 1px solid #e9d5ff;
          border-radius: 8px;
          padding: 12px 16px;
          margin-bottom: 8px;
        }
        .property-card:last-child {
          margin-bottom: 0;
        }

        .property-info {
          flex: 1;
        }
        .property-address {
          margin: 0;
          font-weight: 500;
          font-size: 0.9rem;
          color: #374151;
        }
        .property-city {
          margin: 0;
          font-size: 0.8rem;
          color: #6b7280;
        }
        .property-services {
          display: flex;
          gap: 4px;
          margin-top: 6px;
          flex-wrap: wrap;
        }
        .service-badge-sm {
          font-size: 0.65rem;
          background: #e0e7ff;
          color: #3730a3;
          padding: 2px 6px;
          border-radius: 3px;
        }
        .service-badge-sm.salt {
          background: #d1fae5;
          color: #059669;
        }

        .tier-badge-sm {
          font-size: 0.65rem;
          background: #e0e7ff;
          color: #3730a3;
          padding: 2px 6px;
          border-radius: 3px;
          font-weight: 500;
        }

        .property-pricing {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 2px;
        }

        .property-price {
          font-size: 1.1rem;
          font-weight: 600;
          color: #7c3aed;
        }

        .property-payment {
          font-size: 0.75rem;
          color: #6b7280;
        }

        .property-actions {
          display: flex;
          gap: 6px;
        }

        .btn-xs {
          font-size: 0.7rem;
          padding: 4px 8px;
        }

        /* Property modal */
        .modal-sm {
          max-width: 480px;
        }
        .property-parent-info {
          background: #f5f3ff;
          border: 1px solid #ddd6fe;
          border-radius: 6px;
          padding: 10px 12px;
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .parent-label {
          font-size: 0.8rem;
          color: #6b7280;
        }
        .parent-name {
          font-weight: 600;
          color: #7c3aed;
        }

        @media (max-width: 768px) {
          .desktop-nav {
            display: none;
          }
          .mobile-menu-btn {
            display: flex;
          }
          .form-row, .form-row-3 {
            grid-template-columns: 1fr;
          }
          .customer-card-header {
            flex-direction: column;
            gap: 8px;
          }
          .customer-price {
            font-size: 1.25rem;
          }
        }
      
        /* Print View Styles */
        .header-buttons {
          display: flex;
          gap: 10px;
          align-items: center;
        }

        .btn-secondary-outline {
          background: transparent;
          border: 1px solid #d1d5db;
          color: #374151;
          display: flex;
          align-items: center;
          gap: 6px;
        }

        .btn-secondary-outline:hover {
          background: #f3f4f6;
          border-color: #9ca3af;
        }

        .btn-icon {
          width: 18px;
          height: 18px;
        }

        .print-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: white;
          z-index: 1000;
          overflow-y: auto;
        }

        .print-container {
          max-width: 800px;
          margin: 0 auto;
          padding: 20px;
        }

        .print-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 16px 0;
          border-bottom: 2px solid #e5e7eb;
          margin-bottom: 24px;
        }

        .print-header h2 {
          margin: 0;
          font-size: 1.25rem;
          color: #1f2937;
        }

        .print-actions {
          display: flex;
          gap: 12px;
        }

        .print-page-header {
          text-align: center;
          padding-bottom: 20px;
          border-bottom: 2px solid #1f2937;
          margin-bottom: 24px;
        }

        .print-page-header h1 {
          margin: 0 0 8px 0;
          font-size: 1.75rem;
          color: #1f2937;
        }

        .print-date {
          margin: 0;
          color: #6b7280;
          font-size: 0.9rem;
        }

        .print-count {
          margin: 4px 0 0 0;
          color: #374151;
          font-weight: 500;
        }

        .print-customer-list {
          display: flex;
          flex-direction: column;
          gap: 16px;
        }

        .print-customer-card {
          border: 1px solid #d1d5db;
          border-radius: 8px;
          padding: 16px;
          page-break-inside: avoid;
        }

        .print-customer-header {
          display: flex;
          align-items: center;
          gap: 12px;
          padding-bottom: 12px;
          border-bottom: 1px solid #e5e7eb;
          margin-bottom: 12px;
        }

        .print-customer-number {
          background: #1f2937;
          color: white;
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.85rem;
          font-weight: 600;
          flex-shrink: 0;
        }

        .print-customer-name {
          margin: 0;
          flex: 1;
          font-size: 1.1rem;
          color: #1f2937;
        }

        .print-customer-price {
          font-size: 1.1rem;
          font-weight: 600;
          color: #059669;
        }

        .print-customer-details {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .print-detail-row {
          display: flex;
          gap: 8px;
          font-size: 0.9rem;
        }

        .print-label {
          font-weight: 500;
          color: #6b7280;
          min-width: 70px;
        }

        .print-value {
          color: #1f2937;
        }

        .print-services {
          display: flex;
          gap: 6px;
          flex-wrap: wrap;
        }

        .print-service-tag {
          background: #f3f4f6;
          border: 1px solid #d1d5db;
          padding: 2px 8px;
          border-radius: 4px;
          font-size: 0.8rem;
        }

        .print-service-tag.salt {
          background: #fef3c7;
          border-color: #fcd34d;
          color: #92400e;
        }

        .print-notes {
          margin-top: 8px;
          padding-top: 8px;
          border-top: 1px dashed #e5e7eb;
        }

        .print-notes .print-value {
          font-style: italic;
          color: #4b5563;
        }

        .print-properties {
          margin-top: 12px;
          padding-top: 12px;
          border-top: 1px solid #e5e7eb;
        }

        .print-properties-label {
          font-size: 0.85rem;
          font-weight: 500;
          color: #6b7280;
          display: block;
          margin-bottom: 8px;
        }

        .print-property {
          display: flex;
          justify-content: space-between;
          padding: 4px 0 4px 16px;
          font-size: 0.85rem;
        }

        .print-property-address {
          color: #374151;
        }

        .print-property-price {
          color: #059669;
          font-weight: 500;
        }

        .print-footer {
          text-align: center;
          padding-top: 24px;
          margin-top: 24px;
          border-top: 1px solid #e5e7eb;
          color: #9ca3af;
          font-size: 0.85rem;
        }

        /* Print-specific styles */
        @media print {
          body * {
            visibility: hidden;
          }

          .print-overlay,
          .print-overlay * {
            visibility: visible;
          }

          .print-overlay {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }

          .no-print {
            display: none !important;
          }

          .print-container {
            max-width: 100%;
            padding: 0;
          }

          .print-customer-card {
            border: 1px solid #ccc;
            margin-bottom: 12px;
            page-break-inside: avoid;
          }

          .print-page-header {
            padding-bottom: 16px;
          }

          .print-page-header h1 {
            font-size: 1.5rem;
          }

          .print-customer-number {
            background: #333;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .print-service-tag {
            border: 1px solid #999;
            background: #f5f5f5;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .print-service-tag.salt {
            background: #fff3cd;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }

          .print-footer {
            position: fixed;
            bottom: 0;
            left: 0;
            right: 0;
            text-align: center;
          }

          @page {
            margin: 0.5in;
          }
        }
      `}</style>
    </div>
  );
}

export default Customers;
